import {
  type LaunchRefusal,
  estimateGapSentence,
  estimateHeadline,
  estimateMethodSentence,
} from '@/lib/npc-launch';
import type { NpcEstimate } from '@matter_extractor/shared';
import { Alert, Spinner } from '@pms-digital/design-system';

/**
 * L'estimation d'un lancement, au bas de la boîte (npc.md §6.2, N19, H14 ; maquette `Lancement`,
 * états a, b et e) : combien de rejeux et combien d'euros, côté dispositif et côté joueur à part,
 * et DE QUOI c'est fait. Elle dit toujours que ce n'est pas un plafond : rien ne borne la dépense
 * d'une série.
 *
 * Trois états, lus de `data-state` : `reading` (la lecture est en vol), `ready` (une estimation, ou
 * « estimation impossible » quand rien ne permet de la faire) et `refused` (le refus que le
 * lancement répondrait : une expérience sans page protocole, un banc vide...). Un refus n'est jamais
 * un trou dans la boîte : il dit quoi faire, et le bouton est fermé derrière lui.
 */

export interface LaunchEstimateProps {
  estimate: NpcEstimate | undefined;
  refusal: LaunchRefusal | null;
  /** The read failed for a reason this box has no sentence for. */
  failed: boolean;
  pending: boolean;
  player: 'server' | 'claude';
  experienceName: string;
  /** « Claude Sonnet 5.5 », the tariff the player is counted at, or null for Claude. */
  playerModel: string | null;
}

export function LaunchEstimate({
  estimate,
  refusal,
  failed,
  pending,
  player,
  experienceName,
  playerModel,
}: LaunchEstimateProps) {
  if (refusal !== null) {
    return (
      <div data-testid="npc-lancement-estimation" data-state="refused">
        <div data-testid="npc-lancement-refus">
          <Alert tone="warning" title={refusal.title}>
            {refusal.sentence}
          </Alert>
        </div>
      </div>
    );
  }
  if (failed) {
    return (
      <p
        data-testid="npc-lancement-estimation"
        data-state="failed"
        className="text-gray-700 text-small"
      >
        L’estimation n’a pas pu être faite. Le lancement reste possible : le coût se lira sur chaque
        ligne de la série.
      </p>
    );
  }
  if (estimate === undefined || pending) {
    return (
      <div
        data-testid="npc-lancement-estimation"
        data-state="reading"
        className="flex items-center gap-2 text-gray-600 text-small"
      >
        <Spinner label="Estimation en cours" />
      </div>
    );
  }
  const gap = estimateGapSentence(estimate);
  return (
    <div
      data-testid="npc-lancement-estimation"
      data-state="ready"
      data-impossible={estimate.impossible ? 'true' : 'false'}
      className="space-y-1.5 rounded-md border-2 border-gray-200 bg-gray-50 px-4 py-3"
    >
      <p data-testid="npc-lancement-estimation-titre" className="font-semibold text-gray-900">
        {estimateHeadline(estimate, player)}
      </p>
      <p
        data-testid="npc-lancement-estimation-methode"
        className="text-caption text-gray-600 leading-4"
      >
        {estimateMethodSentence(estimate, experienceName, player === 'server' ? playerModel : null)}
      </p>
      {gap === null ? null : <p className="text-caption text-gray-600 leading-4">{gap}</p>}
      {estimate.skipped.length === 0 ? null : (
        <p data-testid="npc-lancement-sautes" className="text-caption text-gray-600 leading-4">
          {estimate.skipped.map((one) => one.name).join(', ')}{' '}
          {estimate.skipped.length > 1 ? 'sont retirés du banc' : 'est retiré du banc'} : le
          lancement {estimate.skipped.length > 1 ? 'les saute' : 'le saute'}.
        </p>
      )}
    </div>
  );
}
